/**
 * Language Switcher
 * Auto-generates floating language switcher UI from data-lionrapid-switcher attributes.
 * All styles are fixed-position overlays — the style only changes the visual appearance.
 *
 * Styles:
 * - floating: button showing current language → click opens dropdown menu
 * - buttons: row of language code buttons (EN, ES, FR)
 * - flags: row of flag + name buttons
 */

import type { LionRapidCore } from '../lionRapid.js';

export type SwitcherStyle = 'flags' | 'buttons' | 'floating';

export interface Language {
  code: string;
  name: string;
  flag?: string;
}

// Default language data (expandable)
const DEFAULT_LANGUAGES: Record<string, Language> = {
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  es: { code: 'es', name: 'Español', flag: '🇪🇸' },
  fr: { code: 'fr', name: 'Français', flag: '🇫🇷' },
  de: { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  it: { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  pt: { code: 'pt', name: 'Português', flag: '🇵🇹' },
  nl: { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  pl: { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  ru: { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  ja: { code: 'ja', name: '日本語', flag: '🇯🇵' },
  ko: { code: 'ko', name: '한국어', flag: '🇰🇷' },
  zh: { code: 'zh', name: '中文', flag: '🇨🇳' },
  ar: { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  hi: { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  tr: { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  vi: { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  th: { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  sv: { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  da: { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  fi: { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  no: { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  cs: { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  el: { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  he: { code: 'he', name: 'עברית', flag: '🇮🇱' },
  id: { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  ms: { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
  uk: { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  ro: { code: 'ro', name: 'Română', flag: '🇷🇴' },
  hu: { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  sk: { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  bg: { code: 'bg', name: 'Български', flag: '🇧🇬' },
  hr: { code: 'hr', name: 'Hrvatski', flag: '🇭🇷' },
  sr: { code: 'sr', name: 'Српски', flag: '🇷🇸' },
  sl: { code: 'sl', name: 'Slovenščina', flag: '🇸🇮' },
  lt: { code: 'lt', name: 'Lietuvių', flag: '🇱🇹' },
  lv: { code: 'lv', name: 'Latviešu', flag: '🇱🇻' },
  et: { code: 'et', name: 'Eesti', flag: '🇪🇪' },
};

// All switcher styles are floating overlays — position: fixed with configurable corner
const STYLES = `
.lionrapid-switcher {
  position: fixed;
  z-index: 999999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
}
.lionrapid-switcher[data-position="top-left"] { top: 20px; left: 20px; }
.lionrapid-switcher[data-position="top-right"] { top: 20px; right: 20px; }
.lionrapid-switcher[data-position="bottom-left"] { bottom: 20px; left: 20px; }
.lionrapid-switcher[data-position="bottom-right"] { bottom: 20px; right: 20px; }

/* Floating style: button + dropdown menu */
.lionrapid-floating-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  font-size: 14px;
  color: #333;
}
.lionrapid-floating-menu {
  display: none;
  position: absolute;
  margin: 4px 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  min-width: 150px;
}
.lionrapid-floating-menu.open { display: block; }
.lionrapid-floating-menu button {
  display: block;
  width: 100%;
  padding: 8px 16px;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  color: #333;
  white-space: nowrap;
  font-size: 14px;
}
.lionrapid-floating-menu button:hover { background: #f5f5f5; }
.lionrapid-floating-menu button.active { font-weight: 700; background: #f0f0f0; }

/* Buttons + Flags styles: row of buttons */
.lionrapid-btn-row {
  display: flex;
  gap: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}
.lionrapid-btn-row button {
  padding: 8px 12px;
  font-size: 14px;
  border: none;
  border-right: 1px solid #eee;
  background: #fff;
  cursor: pointer;
  color: #333;
  white-space: nowrap;
  transition: background 0.15s;
}
.lionrapid-btn-row button:last-child { border-right: none; }
.lionrapid-btn-row button:hover { background: #f5f5f5; }
.lionrapid-btn-row button.active { background: #3b82f6; color: #fff; }
`;

export class LanguageSwitcher {
  private core: LionRapidCore | null = null;
  private languages: Language[] = [];
  private initialLocale = 'en';
  private onLanguageChange: ((locale: string) => void) | null = null;
  private stylesInjected = false;

  constructor() {}

  /**
   * Set the core instance
   */
  setCore(core: LionRapidCore): void {
    this.core = core;
  }

  /**
   * Set initial locale (used before core is available)
   */
  setInitialLocale(locale: string): void {
    this.initialLocale = locale;
  }

  /**
   * Set available languages
   */
  setLanguages(languages: Language[] | string[]): void {
    this.languages = languages.map((lang) => {
      if (typeof lang === 'string') {
        return DEFAULT_LANGUAGES[lang] || { code: lang, name: lang };
      }
      return lang;
    });
  }

  /**
   * Set callback for language changes
   */
  setOnLanguageChange(callback: (locale: string) => void): void {
    this.onLanguageChange = callback;
  }

  /**
   * Initialize all switchers on the page
   */
  initAll(): void {
    this.injectStyles();
    const switchers = document.querySelectorAll('[data-lionrapid-switcher]');
    switchers.forEach((element) => this.initSwitcher(element as HTMLElement));
  }

  /**
   * Initialize a single switcher element
   */
  initSwitcher(element: HTMLElement): void {
    const style = (element.dataset.lionrapidSwitcher || 'floating') as SwitcherStyle;
    const currentLocale = this.core?.getCurrentLocale() || this.initialLocale;

    // Clear existing content
    element.innerHTML = '';
    element.classList.add('lionrapid-switcher');
    element.setAttribute('data-no-translate', '');
    element.setAttribute('translate', 'no');

    // Read config from data attributes
    const position = element.dataset.lionrapidSwitcherPosition || 'top-right';
    const showFlags = element.dataset.lionrapidSwitcherShowFlags !== 'false';
    const showNames = element.dataset.lionrapidSwitcherShowNames !== 'false';
    const customCss = element.dataset.lionrapidSwitcherCustomCss || null;

    // Set position for CSS
    element.dataset.position = position;

    // Inject custom CSS if provided
    if (customCss) {
      const styleEl = document.createElement('style');
      styleEl.textContent = customCss;
      document.head.appendChild(styleEl);
    }

    switch (style) {
      case 'flags':
        this.renderButtonRow(element, currentLocale, true, true);
        break;
      case 'buttons':
        this.renderButtonRow(element, currentLocale, showFlags, showNames);
        break;
      case 'floating':
      default:
        this.renderFloating(element, currentLocale, position, showFlags, showNames);
        break;
    }
  }

  /**
   * Render floating style (button showing current language → click opens dropdown menu)
   */
  private renderFloating(
    container: HTMLElement,
    currentLocale: string,
    position: string,
    showFlags: boolean,
    showNames: boolean,
  ): void {
    // Menu position: anchor above or below button, left or right aligned
    const menuStyle: Partial<CSSStyleDeclaration> = {};
    if (position.startsWith('bottom')) {
      menuStyle.bottom = '100%';
    } else {
      menuStyle.top = '100%';
    }
    if (position.endsWith('left')) {
      menuStyle.left = '0';
    } else {
      menuStyle.right = '0';
    }

    const formatLabel = (lang: Language): string => {
      const flag = showFlags ? (lang.flag || '') : '';
      const name = showNames ? lang.name : lang.code.toUpperCase();
      return `${flag}${flag && name ? ' ' : ''}${name}`;
    };

    // Current language display
    const currentLang = this.languages.find((l) => l.code === currentLocale) || this.languages[0];
    const currentLabel = currentLang ? formatLabel(currentLang) : currentLocale;

    // Button
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lionrapid-floating-btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-haspopup', 'true');
    btn.textContent = `${currentLabel} \u25BE`;

    // Menu
    const menu = document.createElement('div');
    menu.className = 'lionrapid-floating-menu';
    Object.assign(menu.style, menuStyle);

    for (const lang of this.languages) {
      const item = document.createElement('button');
      item.type = 'button';
      item.dataset.lang = lang.code;
      item.textContent = formatLabel(lang);
      if (lang.code === currentLocale) {
        item.classList.add('active');
      }
      item.addEventListener('click', () => {
        this.handleLanguageChange(lang.code);
        btn.textContent = formatLabel(lang) + ' \u25BE';
        menu.querySelectorAll('button').forEach((b) => {
          b.classList.toggle('active', b.dataset.lang === lang.code);
        });
        menu.classList.remove('open');
        btn.setAttribute('aria-expanded', 'false');
      });
      menu.appendChild(item);
    }

    // Toggle menu on button click
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = menu.classList.contains('open');
      menu.classList.toggle('open', !isOpen);
      btn.setAttribute('aria-expanded', String(!isOpen));
    });

    // Close on outside click
    document.addEventListener('click', () => {
      menu.classList.remove('open');
      btn.setAttribute('aria-expanded', 'false');
    });

    container.appendChild(btn);
    container.appendChild(menu);
  }

  /**
   * Render button row style (used for both "buttons" and "flags")
   */
  private renderButtonRow(
    container: HTMLElement,
    currentLocale: string,
    showFlags: boolean,
    showNames: boolean,
  ): void {
    const row = document.createElement('div');
    row.className = 'lionrapid-btn-row';

    for (const lang of this.languages) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.lang = lang.code;

      const flag = showFlags ? (lang.flag || '') : '';
      const name = showNames ? lang.name : lang.code.toUpperCase();
      button.textContent = `${flag}${flag && name ? ' ' : ''}${name}`;

      if (lang.code === currentLocale) {
        button.classList.add('active');
      }

      button.addEventListener('click', () => {
        this.handleLanguageChange(lang.code);
        row.querySelectorAll('button').forEach((btn) => {
          btn.classList.toggle('active', btn.dataset.lang === lang.code);
        });
      });

      row.appendChild(button);
    }

    container.appendChild(row);
  }

  /**
   * Handle language change — update all switchers on page + fire callback
   */
  private handleLanguageChange(locale: string): void {
    if (this.onLanguageChange) {
      this.onLanguageChange(locale);
    }

    // Update all button row states
    document.querySelectorAll('.lionrapid-btn-row button').forEach((btn) => {
      const button = btn as HTMLButtonElement;
      button.classList.toggle('active', button.dataset.lang === locale);
    });
  }

  /**
   * Inject minimal styles
   */
  private injectStyles(): void {
    if (this.stylesInjected) return;

    const styleElement = document.createElement('style');
    styleElement.textContent = STYLES;
    document.head.appendChild(styleElement);
    this.stylesInjected = true;
  }

  /**
   * Get language info by code
   */
  static getLanguage(code: string): Language | undefined {
    return DEFAULT_LANGUAGES[code];
  }
}
